import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiHeart, FiRefreshCw, FiTruck } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/main.scss";

function Main() {
  const { toggleWishlist, isInWishlist } = useContext(CartContext);
  const newArrivals = productData.filter((product) => product.tags.includes("new-in")).slice(0, 4);
  const favourites = productData.filter((product) => product.tags.includes("featured") && !newArrivals.includes(product)).slice(0, 4);
  const womenHero = productData.find((product) => product.gender === "Women") || productData[0];
  const menHero = productData.find((product) => product.gender === "Men") || productData[0];
  const editorialProduct = productData.find((product) => product.tags.includes("sale")) || womenHero;
  const manifestoWomen = productData.find((product) => product.category === "Jumpsuits") || womenHero;
  const manifestoMen = productData.find((product) => product.gender === "Men" && product.category === "Jackets") || menHero;
  const preorderProduct = productData.find((product) => product.tags.includes("pre-order") && product.id !== womenHero.id) || womenHero;

  const renderProductCard = (product) => (
    <article className="home-product" key={product.id}>
      <div className="home-product__media">
        <Link to={`/shopDetails/${product.id}`} aria-label={`${product.name}, ${product.colorName}`}>
          <img src={product.image} alt={`${product.name} in ${product.colorName}`} loading="lazy" />
        </Link>
        <div className="home-product__badges">
          {product.badges.map((badge) => <span key={badge}>{badge}</span>)}
        </div>
        <button
          type="button"
          className={isInWishlist(product.id) ? "is-active" : ""}
          onClick={() => toggleWishlist(product.id)}
          aria-label={`${isInWishlist(product.id) ? "Remove" : "Add"} ${product.name} ${isInWishlist(product.id) ? "from" : "to"} wishlist`}
        >
          <FiHeart />
        </button>
      </div>
      <Link className="home-product__info" to={`/shopDetails/${product.id}`}>
        <span>
          <strong>{product.name}</strong>
          <small>{product.colorName}</small>
        </span>
        <span className="home-product__price">
          {formatPrice(product.price)}
          {product.compareAt && <del>{formatPrice(product.compareAt)}</del>}
        </span>
      </Link>
      <div className="home-product__sizes" aria-label="Available sizes">
        {product.sizes.map((size) => <span key={size}>{size}</span>)}
      </div>
    </article>
  );

  return (
    <main className="home-page">
      <section className="home-hero" aria-labelledby="home-hero-title">
        <Link className="home-hero__image home-hero__image--women" to="/shop?collection=women" aria-label="Shop women">
          <img src={womenHero.image} alt={`${womenHero.name} for women, photographed by the coast`} />
          <span>Women</span>
        </Link>
        <Link className="home-hero__image home-hero__image--men" to="/shop?collection=men" aria-label="Shop men">
          <img src={menHero.image} alt={`${menHero.name} for men, photographed by the coast`} />
          <span>Men</span>
        </Link>
        <div className="home-hero__content">
          <p className="eyebrow">{siteConfig.commerce.seasonLabel}</p>
          <h1 id="home-hero-title">Made of<br />the sea.</h1>
          <p>Natural textures, sun-washed colour and pieces designed to stay.</p>
          <div>
            <Link className="button-light" to="/shop?collection=women">Shop women</Link>
            <Link className="button-light" to="/shop?collection=men">Shop men</Link>
          </div>
        </div>
      </section>

      <section className="home-assurances" aria-label="Store assurances">
        <div><span>01</span><strong>Considered materials</strong><small>Natural, recycled and botanic fibres.</small></div>
        <div><span>02</span><strong>Made in Europe</strong><small>Long-term partners and careful craft.</small></div>
        <div><span>03</span><strong>Fewer, better pieces</strong><small>Demand-led quantities that reduce waste.</small></div>
      </section>

      <section className="home-products page-shell">
        <div className="section-heading-row">
          <div>
            <p className="eyebrow">Just landed</p>
            <h2>New from the coast</h2>
          </div>
          <Link className="text-link" to="/shop?status=new-in">View all <FiArrowRight /></Link>
        </div>
        <div className="home-product-grid">{newArrivals.map(renderProductCard)}</div>
      </section>

      <section className="home-editorial">
        <div className="home-editorial__image">
          <img src={editorialProduct.image} alt={`${editorialProduct.name} in ${editorialProduct.colorName} on a coastal path`} loading="lazy" />
        </div>
        <div className="home-editorial__copy">
          <p className="eyebrow">Island states</p>
          <h2>Colour found in nature.</h2>
          <p>Deep forest, salt white and the many blues between sky and sea. A collection built from the colours that make us feel at home.</p>
          <Link className="text-link" to="/shop?collection=women">Explore the edit <FiArrowRight /></Link>
        </div>
      </section>

      <section className="home-manifesto page-shell">
        <div className="home-manifesto__collage">
          <img src={manifestoWomen.image} alt={`${manifestoWomen.name} in a coastal landscape`} loading="lazy" />
          <img src={manifestoMen.image} alt={`${manifestoMen.name} on a salt-marsh boardwalk`} loading="lazy" />
        </div>
        <div className="home-manifesto__copy">
          <p className="eyebrow">Our north star</p>
          <h2>Two thirds of our planet is ocean. It connects every choice we make.</h2>
          <p>{siteConfig.description} We begin with the fibre, produce with trusted European makers and design beyond a single season.</p>
          <Link className="button-secondary" to="/about">Read our story</Link>
        </div>
      </section>

      <section className="home-quote">
        <p className="eyebrow">A slower rhythm</p>
        <blockquote>“Buy less. Choose well.<br />Wear it into your story.”</blockquote>
      </section>

      <section className="home-products page-shell">
        <div className="section-heading-row">
          <div>
            <p className="eyebrow">Community favourites</p>
            <h2>Worn on repeat</h2>
          </div>
          <Link className="text-link" to="/shop">Shop all <FiArrowRight /></Link>
        </div>
        <div className="home-product-grid">{favourites.map(renderProductCard)}</div>
      </section>

      <section className="home-preorder">
        <div className="home-preorder__image">
          <img src={preorderProduct.image} alt={`${preorderProduct.name} in ${preorderProduct.colorName} on the Atlantic coast`} loading="lazy" />
        </div>
        <div className="home-preorder__copy">
          <p className="eyebrow">Our pre-order system</p>
          <h2>We make what you choose.</h2>
          <p>Pre-order lets us understand real demand before production begins. It means less overstock, fewer wasted resources and more time to make each piece properly.</p>
          <Link className="button-light" to="/shop?status=pre-order">Shop pre-order</Link>
        </div>
      </section>

      <section className="home-service page-shell" aria-label="Shopping benefits">
        <div><FiTruck /><strong>Free shipping over {formatPrice(siteConfig.commerce.shippingThreshold)}</strong><span>Tracked delivery from our European warehouse.</span></div>
        <div><FiRefreshCw /><strong>{siteConfig.commerce.returnWindow}-day returns</strong><span>Time to try every piece at home, without the rush.</span></div>
        <div><span className="home-service__mark">{siteConfig.logo.symbol}</span><strong>Here when you need us</strong><span>Real customer care, Monday to Friday.</span></div>
      </section>
    </main>
  );
}

export default Main;
